import { SelectTypes } from '@genial/components';
import {
  CoeOfferDetailEntity,
  instanceOfErrorListResponse,
  ErrorListResponse,
} from '@genial/services';
import { useEffect, useState } from 'react';

import { useRecoilQuery } from '../../../../../../../hooks/useRecoilQuery';
import { useToastCreator } from '../../../../../../../hooks/useToastCreator';
import { CoeOfferDetailsQuery as coeOfferDetailsQuery } from '../../../../../../../store/batchTicket/selectors';
import { AssetSelectedTypeMap } from '../../../../../../../store/product/types';

const billingTypeSuccessMessage = [
  'success',
  'Tudo certo!',
  'Tipo de boletagem alterada com sucesso!',
  { duration: 3000 },
] as const;

export type DateErrorMessage = { message: string };

interface CoesBillingTypeControllerProps {
  product: AssetSelectedTypeMap['coes'];
}

const normalizeBillingTypeOptions = (data?: CoeOfferDetailEntity): SelectTypes.Option[] => {
  const defaultLabel = 'Default';

  return (
    data?.allowedBillingType?.map(({ billingDescription, billingTypeId }) => ({
      label: billingDescription?.trim() || defaultLabel,
      value: billingTypeId?.toString(),
    })) || []
  );
};

export const useCoesBillingTypeController = ({ product }: CoesBillingTypeControllerProps) => {
  const { showToast, showToastFromError } = useToastCreator('coes-billing-type-toast');

  const [billingTypeSelected, setBillingTypeSelected] = useState<string>();
  const [billingTypeOptions, setBillingTypeOptions] = useState<SelectTypes.Option[]>([]);

  const { data, error, loading, refresher } = useRecoilQuery(
    coeOfferDetailsQuery({ productId: product.productIdNormalized })
  );

  const onChangeBillingTypeSelected = (value: string) => {
    if (value === billingTypeSelected) {
      return;
    }
    setBillingTypeSelected(value);
    showToast(...billingTypeSuccessMessage);
  };

  useEffect(() => {
    if (error) {
      if (instanceOfErrorListResponse(error)) {
        (error as unknown as ErrorListResponse).errors?.forEach((errorItem) =>
          showToastFromError(errorItem)
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  useEffect(() => {
    if (!loading && data?.data) {
      const options = normalizeBillingTypeOptions(data.data);
      setBillingTypeOptions(options);
      if (options.length === 1) {
        setBillingTypeSelected(options[0].value);
      }
    }
  }, [loading, data]);

  return {
    refresher,
    billingTypeSelected,
    onChangeBillingTypeSelected,
    billingTypeOptions,
    isLoading: loading,
    applicationIn: data?.data?.applicationIn,
  };
};
